import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Building2, Check, AlertCircle, Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ABMApp } from "@/types/application";
import { cn } from "@/lib/utils";

interface ABMConnection {
  id: string;
  name: string;
  organization_id: string;
  is_active: boolean;
}

interface ABMAppsSelectProps {
  onSelectApp: (app: ABMApp) => void;
  selectedAppId?: string;
}

// Mock ABM apps for demonstration - in production this would come from ABM API
const getMockABMApps = (connectionId: string, connectionName: string): ABMApp[] => [
  {
    id: `abm-${connectionId}-1`,
    name: "Microsoft Word",
    bundleId: "com.microsoft.word",
    icon: "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/d3/59/3e/d3593e96-c6f5-4ec0-9e51-e4a4abf67f06/MSWD.png/100x100bb.png",
    version: "2.81",
    licenseTotal: 100,
    licenseUsed: 45,
    abmConnectionId: connectionId,
    abmConnectionName: connectionName,
    platform: 'ios',
  },
  {
    id: `abm-${connectionId}-2`,
    name: "Microsoft Excel",
    bundleId: "com.microsoft.excel",
    icon: "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/7e/8e/d1/7e8ed12c-6e32-b5b8-caa8-4ff31b9b6e3e/XLSX.png/100x100bb.png",
    version: "2.81",
    licenseTotal: 100,
    licenseUsed: 42,
    abmConnectionId: connectionId,
    abmConnectionName: connectionName,
    platform: 'ios',
  },
  {
    id: `abm-${connectionId}-3`,
    name: "Slack",
    bundleId: "com.tinyspeck.chatlyio",
    icon: "https://is1-ssl.mzstatic.com/image/thumb/Purple116/v4/a8/76/c9/a876c968-8f82-0e58-d9c1-f7c61ac3b804/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/100x100bb.png",
    version: "23.11.10",
    licenseTotal: 50,
    licenseUsed: 50,
    abmConnectionId: connectionId,
    abmConnectionName: connectionName,
    platform: 'ios',
  },
  {
    id: `abm-${connectionId}-4`,
    name: "Zoom",
    bundleId: "us.zoom.videomeetings",
    icon: "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/a8/92/cc/a892ccd0-c0e0-9b70-ce15-8cf0e87f6d8e/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/100x100bb.png",
    version: "5.17.0",
    licenseTotal: 75,
    licenseUsed: 30,
    abmConnectionId: connectionId,
    abmConnectionName: connectionName,
    platform: 'ios',
  },
];

export const ABMAppsSelect = ({ onSelectApp, selectedAppId }: ABMAppsSelectProps) => {
  const [connections, setConnections] = useState<ABMConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string>("");
  const [apps, setApps] = useState<ABMApp[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch ABM connections
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data, error } = await supabase
          .from('abm_connections')
          .select('id, name, organization_id, is_active')
          .eq('is_active', true)
          .order('name');
        
        if (error) throw error;
        setConnections(data || []);
        
        // Auto-select first connection if only one
        if (data && data.length === 1) {
          setSelectedConnection(data[0].id);
        }
      } catch (err) {
        console.error('Error fetching ABM connections:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
  }, []);

  // Fetch apps when connection changes
  useEffect(() => {
    if (selectedConnection) {
      const connection = connections.find(c => c.id === selectedConnection);
      if (connection) {
        // In production, this would call an ABM API edge function
        // For now, use mock data
        const mockApps = getMockABMApps(selectedConnection, connection.name);
        setApps(mockApps);
      }
    } else {
      setApps([]);
    }
  }, [selectedConnection, connections]);

  const filteredApps = apps.filter(app =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.bundleId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLicenseBadgeVariant = (used: number, total: number): "default" | "secondary" | "destructive" | "outline" => {
    const ratio = used / total;
    if (ratio >= 1) return "destructive";
    if (ratio >= 0.8) return "secondary";
    return "outline";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading ABM accounts...
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground border rounded-lg bg-muted/30">
        <AlertCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No ABM Accounts Connected</p>
        <p className="text-sm mt-1">Connect an Apple Business Manager account to import apps</p>
        <Button variant="outline" className="mt-4" size="sm">
          Connect ABM Account
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ABM Account Selector */}
      <div className="flex gap-3">
        <Select value={selectedConnection} onValueChange={setSelectedConnection}>
          <SelectTrigger className="w-[280px]">
            <Building2 className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Select ABM Account" />
          </SelectTrigger>
          <SelectContent>
            {connections.map((conn) => (
              <SelectItem key={conn.id} value={conn.id}>
                {conn.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedConnection && (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              className="pl-9" 
              placeholder="Filter apps..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Apps List */}
      {selectedConnection ? (
        filteredApps.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application</TableHead>
                  <TableHead>ABM Account</TableHead>
                  <TableHead>Licenses</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.map((app) => (
                  <TableRow 
                    key={app.id}
                    className={cn(
                      "cursor-pointer transition-colors",
                      selectedAppId === app.id && "bg-muted",
                      app.licenseUsed >= app.licenseTotal && "opacity-60"
                    )}
                    onClick={() => onSelectApp(app)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border">
                          <img 
                            src={app.icon} 
                            alt={app.name} 
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder.svg';
                            }}
                          />
                        </div>
                        <div>
                          <div className="font-medium">{app.name}</div>
                          <div className="text-xs text-muted-foreground">{app.bundleId}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        <Building2 className="h-3 w-3 mr-1" />
                        {app.abmConnectionName}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getLicenseBadgeVariant(app.licenseUsed, app.licenseTotal)}>
                        {app.licenseTotal - app.licenseUsed}/{app.licenseTotal} available
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant={selectedAppId === app.id ? "default" : "outline"} 
                        size="sm"
                        disabled={app.licenseUsed >= app.licenseTotal}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectApp(app);
                        }}
                      >
                        {selectedAppId === app.id ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Selected
                          </>
                        ) : app.licenseUsed >= app.licenseTotal ? (
                          "No Licenses"
                        ) : (
                          "Select"
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground border rounded-lg">
            <p>No apps found matching "{searchTerm}"</p>
          </div>
        )
      ) : (
        <div className="text-center py-12 text-muted-foreground border rounded-lg bg-muted/30">
          <Package className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <p>Select an ABM Account</p>
          <p className="text-sm mt-1">Choose an account to view available apps</p>
        </div>
      )}
    </div>
  );
};
